import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  addDays,
  fmtDateWeek,
  fmtMonthDay,
  type AdminMemberDTO,
  type AdminTeamDTO,
  type OffboardPreviewDTO,
  type OfficialHolidaysDTO,
  type Role,
  type SettingsDTO,
} from '@wd/shared';
import { api, errorText } from '../../lib/api';
import { todayCN } from '../../lib/clock';
import { readTextFile } from '../../lib/textFile';
import { fetchOffboardPreview, useAdminActions, useAdminDirectory, useSettingsActions, type MemberInput } from '../../lib/queries';
import { Icon, StatusIcon } from '../../ui/icons';
import { Popover } from '../../ui/menu';
import { Avatar, Button, EmptyState, IconButton, Modal, Skeleton } from '../../ui/primitives';
import { Choice, Head } from './parts';

// 设置 › 团队与成员（设计稿 22c）与离职交接（22d）。管理员可改，主管只读。

const ROLE_LABEL: Record<Role, string> = { admin: '管理员', manager: '主管', member: '成员' };

function roleText(m: AdminMemberDTO) {
  const base = ROLE_LABEL[m.role];
  if (m.role === 'admin' && m.leads) return `${base} · 主管`;
  if (m.role === 'member' && m.leads) return `${base} · 有下属`;
  return base;
}

function statusText(m: AdminMemberDTO) {
  switch (m.status) {
    case 'active':
      return '已激活';
    case 'invited':
      return '待激活 · 已发邀请';
    case 'offboarding':
      return `离职交接中${m.offboard ? ` · ${fmtMonthDay(m.offboard.lastDay)}` : ''}`;
    case 'left':
      return '已离职';
  }
}

export function MembersSection({ dto, isAdmin, onHolidays }: { dto: SettingsDTO; isAdmin: boolean; onHolidays: () => void }) {
  const { data, isLoading, error } = useAdminDirectory();
  const [editing, setEditing] = useState<AdminMemberDTO | 'new' | null>(null);
  // 从成员页“编辑成员信息”进来：/settings/members?edit=<id>
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const id = params.get('edit');
    if (!id || !data || !isAdmin) return;
    const m = data.members.find((x) => x.id === id);
    if (m) setEditing(m);
    setParams({}, { replace: true });
  }, [params, data, isAdmin, setParams]);
  const [offboarding, setOffboarding] = useState<AdminMemberDTO | null>(null);
  const [importing, setImporting] = useState(false);
  const [showLeft, setShowLeft] = useState(false);

  if (error) return <EmptyState icon="lock" title="看不到成员列表" desc={errorText(error)} />;
  if (isLoading || !data) return <Skeleton w={240} h={20} />;

  const live = data.members.filter((m) => m.status !== 'left');
  const left = data.members.filter((m) => m.status === 'left');
  const invited = live.filter((m) => m.status === 'invited').length;
  const rows = showLeft ? data.members : live;
  const year = todayCN().slice(0, 4);
  const holidaysThisYear = dto.current.holidays.filter((d) => d.startsWith(year)).length;

  return (
    <>
      <Head
        title="团队与成员"
        desc={`${live.length} 名成员 · ${data.teams.length} 个团队${invited ? ` · ${invited} 个待激活` : ''}`}
        right={
          isAdmin && (
            <span className="row" style={{ gap: 8 }}>
              <Button icon="export" onClick={() => setImporting(true)}>
                导入 CSV
              </Button>
              <Button variant="primary" onClick={() => setEditing('new')}>
                邀请成员
              </Button>
            </span>
          )
        }
      />

      <div className="st-table">
        <div className="st-tr head">
          <span className="c-name">成员</span>
          <span className="c-team">团队</span>
          <span className="c-role">角色</span>
          <span className="c-proj">项目</span>
          <span className="c-status">状态</span>
        </div>
        {rows.map((m) => (
          <button
            key={m.id}
            type="button"
            className={clsx('st-tr', m.status === 'left' && 'dim')}
            disabled={!isAdmin}
            onClick={() => setEditing(m)}
          >
            <span className="c-name">
              <Avatar initial={m.initial} seed={m.name} />
              <span className="ellipsis">{m.name}</span>
            </span>
            <span className="c-team ellipsis">{m.teamName ?? (m.title || '—')}</span>
            <span className="c-role ellipsis">{roleText(m)}</span>
            <span className="c-proj">{m.projectCount || '—'}</span>
            <span className={clsx('c-status', m.status === 'offboarding' && 'hot')}>{statusText(m)}</span>
          </button>
        ))}
      </div>
      {left.length > 0 && (
        <button type="button" className="st-more" onClick={() => setShowLeft(!showLeft)}>
          {showLeft ? '隐藏已离职的成员' : `显示已离职的 ${left.length} 人`}
        </button>
      )}

      <TeamsEditor teams={data.teams} isAdmin={isAdmin} />

      <div className="st-cal">
        <Icon name="cal" size={14} />
        <span>
          节假日日历：
          {dto.current.followHolidays
            ? `跟随中国法定节假日（${year}）· 已登记 ${holidaysThisYear} 天休假、${dto.current.extraWorkdays.filter((d) => d.startsWith(year)).length} 天调休上班`
            : '仅周一至周五，不看节假日'}
          {' · '}
        </span>
        <button type="button" className="st-link" onClick={onHolidays}>
          {isAdmin ? '修改' : '查看'}
        </button>
      </div>

      {editing && (
        <MemberModal
          member={editing === 'new' ? null : editing}
          all={data.members}
          teams={data.teams}
          onClose={() => setEditing(null)}
          onOffboard={(m) => {
            setEditing(null);
            setOffboarding(m);
          }}
        />
      )}
      {offboarding && <OffboardModal member={offboarding} all={data.members} onClose={() => setOffboarding(null)} />}
      {importing && <ImportModal onClose={() => setImporting(false)} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// 团队
// ---------------------------------------------------------------------------

function TeamsEditor({ teams, isAdmin }: { teams: AdminTeamDTO[]; isAdmin: boolean }) {
  const { createTeam, renameTeam, deleteTeam } = useAdminActions();
  const [edit, setEdit] = useState<AdminTeamDTO | 'new' | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const anchor = useRef<HTMLElement | null>(null);
  const open = (t: AdminTeamDTO | 'new', el: HTMLElement | null) => {
    anchor.current = el;
    setEdit(t);
    setName(t === 'new' ? '' : t.name);
    setError(null);
  };
  const done = { onSuccess: () => setEdit(null), onError: (e: unknown) => setError(errorText(e)) };
  return (
    <div className="st-teams">
      <span className="t-group">团队</span>
      {teams.map((t) => (
        <button
          key={t.id}
          type="button"
          className="st-team"
          disabled={!isAdmin}
          onClick={(e) => open(t, e.currentTarget)}
        >
          <span className="wd-team-chip">{t.initial}</span>
          {t.name}
          <span className="t-meta">
            {t.memberCount} 人 · {t.projectCount} 个项目
          </span>
        </button>
      ))}
      {isAdmin && (
        <button type="button" className="st-team add" onClick={(e) => open('new', e.currentTarget)}>
          <Icon name="plus" size={12} />
          新建团队
        </button>
      )}
      <Popover anchor={anchor} open={!!edit} onClose={() => setEdit(null)} width={260}>
        <form
          className="wd-pop-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (edit === 'new') createTeam.mutate(name, done);
            else if (edit) renameTeam.mutate({ id: edit.id, name }, done);
          }}
        >
          <input className="wd-input" autoFocus value={name} maxLength={12} placeholder="团队名称，例如：研发中心" onChange={(e) => setName(e.target.value)} />
          {error && <span className="t-meta c-red">{error}</span>}
          <div className="actions">
            {edit && edit !== 'new' && (
              <Button variant="text" onClick={() => deleteTeam.mutate(edit.id, done)}>
                删除
              </Button>
            )}
            <span className="flex1" />
            <Button variant="text" onClick={() => setEdit(null)}>
              取消
            </Button>
            <Button variant="primary" type="submit" disabled={!name.trim()}>
              {edit === 'new' ? '新建' : '保存'}
            </Button>
          </div>
        </form>
      </Popover>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 邀请 / 编辑成员
// ---------------------------------------------------------------------------

function MemberModal({
  member,
  all,
  teams,
  onClose,
  onOffboard,
}: {
  member: AdminMemberDTO | null;
  all: AdminMemberDTO[];
  teams: AdminTeamDTO[];
  onClose: () => void;
  onOffboard: (m: AdminMemberDTO) => void;
}) {
  const navigate = useNavigate();
  const { invite, update, reinvite, cancelOffboard, reassign, revokeMcp } = useAdminActions();
  const [form, setForm] = useState<MemberInput>({
    name: member?.name ?? '',
    email: member?.email ?? '',
    teamId: member?.teamId ?? teams[0]?.id ?? null,
    role: member?.role ?? 'member',
    managerId: member?.managerId ?? all.find((m) => m.role === 'admin')?.id ?? null,
    title: member?.title ?? '',
    responsibility: member?.responsibility ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const set = <K extends keyof MemberInput>(k: K, v: MemberInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const managers = all.filter((m) => m.status !== 'left' && m.id !== member?.id);
  const busy = invite.isPending || update.isPending;
  const live = all.filter((m) => m.status === 'active' && m.id !== member?.id);

  const save = () => {
    const payload = { ...form, email: form.email?.trim() || null, title: form.title?.trim() || null, responsibility: form.responsibility?.trim() || null };
    if (!member) {
      invite.mutate(payload, {
        onSuccess: (r) => (r.mailed ? onClose() : setNote('已加入工作区。邮件服务还没配置，请把登录地址发给对方：对方用这个邮箱收验证码登录。')),
        onError: (e) => setError(errorText(e)),
      });
    } else {
      update.mutate({ id: member.id, ...payload }, { onSuccess: onClose, onError: (e) => setError(errorText(e)) });
    }
  };

  return (
    <Modal
      width={520}
      title={member ? member.name : '邀请成员'}
      subtitle={member ? statusText(member) : '对方用工作邮箱收验证码登录，之后也可以用微信扫码'}
      onClose={onClose}
      footNote={
        member ? (
          <span className="row" style={{ gap: 4 }}>
            {member.status !== 'invited' && (
              <Button variant="text" onClick={() => navigate(`/people/${member.id}`)}>
                成员页
              </Button>
            )}
            {member.status === 'invited' && (
              <Button variant="text" disabled={reinvite.isPending} onClick={() => reinvite.mutate(member.id, { onSuccess: (r) => setNote(r.mailed ? '邀请已重新发送' : '邮件服务还没配置，请直接把登录地址发给对方') })}>
                重发邀请
              </Button>
            )}
            {(member.status === 'active' || member.status === 'invited') && (
              <Button variant="text" onClick={() => onOffboard(member)}>
                离职交接…
              </Button>
            )}
            {member.status === 'offboarding' && (
              <Button variant="text" onClick={() => cancelOffboard.mutate(member.id, { onSuccess: onClose, onError: (e) => setError(errorText(e)) })}>
                取消交接
              </Button>
            )}
            {member.mcpToken && (
              <Button variant="text" disabled={revokeMcp.isPending} onClick={() => revokeMcp.mutate(member.id, { onSuccess: () => setNote(`已停用${member.name}的本地智能体令牌`), onError: (e) => setError(errorText(e)) })}>
                停用 MCP 令牌
              </Button>
            )}
          </span>
        ) : undefined
      }
      footer={
        note ? (
          <Button variant="primary" onClick={onClose}>
            好的
          </Button>
        ) : (
          member?.status !== 'left' && (
            <Button variant="primary" disabled={busy || !form.name.trim() || (!member && !form.email?.trim())} onClick={save}>
              {member ? '保存' : '发送邀请'}
            </Button>
          )
        )
      }
    >
      {note ? (
        <div className="t-body">{note}</div>
      ) : (
        <div className="st-form">
          <label>
            <span className="k">姓名</span>
            <input className="wd-input" value={form.name} maxLength={20} autoFocus={!member} onChange={(e) => set('name', e.target.value)} placeholder="例如：赵工" />
          </label>
          <label>
            <span className="k">工作邮箱</span>
            <input className="wd-input" type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} placeholder="zhao@wattsonic.com" />
          </label>
          <div className="st-form-row">
            <label>
              <span className="k">团队</span>
              <select className="st-native" value={form.teamId ?? ''} onChange={(e) => set('teamId', e.target.value || null)}>
                <option value="">不属于任何团队</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="k">角色</span>
              <select className="st-native" value={form.role} onChange={(e) => set('role', e.target.value as Role)}>
                <option value="member">成员</option>
                <option value="manager">主管：能看本团队日报、周报</option>
                <option value="admin">管理员：还能改设置、管成员</option>
              </select>
            </label>
          </div>
          <label>
            <span className="k">直属主管</span>
            <select className="st-native" value={form.managerId ?? ''} onChange={(e) => set('managerId', e.target.value || null)}>
              <option value="">没有（异常发给本人）</option>
              {managers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.teamName ? ` · ${m.teamName}` : ''}
                </option>
              ))}
            </select>
          </label>
          <div className="st-form-row">
            <label>
              <span className="k">职位（可选）</span>
              <input className="wd-input" value={form.title ?? ''} maxLength={20} onChange={(e) => set('title', e.target.value)} placeholder="例如：硬件工程师" />
            </label>
            <label>
              <span className="k">职责（可选）</span>
              <input className="wd-input" value={form.responsibility ?? ''} maxLength={40} onChange={(e) => set('responsibility', e.target.value)} placeholder="指派时用于推荐，例如：负责本地化材料" />
            </label>
          </div>
          {member?.offboard && (
            <div className="st-handovers">
              <span className="t-group">交接进度 · 最后工作日 {fmtDateWeek(member.offboard.lastDay)}</span>
              {member.offboard.handovers.map((h) => (
                <div key={h.id} className="st-handover">
                  <span className="t-meta">{h.projectCode}</span>
                  <span className="ellipsis flex1">{h.projectName}</span>
                  <span className={clsx('t-meta', h.status === 'declined' && 'c-red')}>
                    {h.status === 'accepted' ? `${h.toName}已接手` : h.status === 'declined' ? `${h.toName}无法接手：${h.declineReason}` : `等${h.toName}确认`}
                  </span>
                  {h.status === 'declined' && (
                    <Choice
                      value=""
                      options={[{ value: '', label: '换人…' }, ...live.map((m) => ({ value: m.id, label: m.name }))]}
                      onChange={(toId) => toId && reassign.mutate({ memberId: member.id, projectId: h.projectId, toId }, { onError: (e) => setError(errorText(e)) })}
                      width={160}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          {error && <div className="t-meta c-red">{error}</div>}
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 离职交接（设计稿 22d）
// ---------------------------------------------------------------------------

function OffboardModal({ member, all, onClose }: { member: AdminMemberDTO; all: AdminMemberDTO[]; onClose: () => void }) {
  const { offboard } = useAdminActions();
  const [preview, setPreview] = useState<OffboardPreviewDTO | null>(null);
  const [lastDay, setLastDay] = useState(addDays(todayCN(), 14));
  const [to, setTo] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const candidates = useMemo(() => all.filter((m) => m.status === 'active' && m.id !== member.id), [all, member.id]);

  useEffect(() => {
    fetchOffboardPreview(member.id)
      .then((p) => {
        setPreview(p);
        setTo(Object.fromEntries(p.projects.map((x) => [x.id, x.suggestedToId && candidates.some((c) => c.id === x.suggestedToId) ? x.suggestedToId : ''])));
      })
      .catch((e) => setError(errorText(e)));
  }, [member.id, candidates]);

  const ready = !!preview && preview.projects.every((p) => to[p.id]) && lastDay >= todayCN();
  return (
    <Modal
      width={560}
      title="离职交接"
      subtitle={`${member.name}${member.teamName ? ` · ${member.teamName}` : ''} · 最后工作日 ${fmtMonthDay(lastDay)}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button
            variant="primary"
            disabled={!ready || offboard.isPending}
            onClick={() =>
              offboard.mutate(
                { id: member.id, lastDay, handovers: Object.entries(to).map(([projectId, toId]) => ({ projectId, toId })) },
                { onSuccess: onClose, onError: (e) => setError(errorText(e)) },
              )
            }
          >
            开始交接
          </Button>
        </>
      }
    >
      {!preview ? (
        error ? <div className="t-meta c-red">{error}</div> : <Skeleton w={200} />
      ) : (
        <div className="st-form">
          <label>
            <span className="k">最后工作日</span>
            <input className="wd-input" type="date" value={lastDay} min={todayCN()} onChange={(e) => setLastDay(e.target.value)} style={{ width: 180 }} />
          </label>
          <div>
            <div className="t-group" style={{ marginBottom: 4 }}>
              负责的项目
            </div>
            {preview.projects.length === 0 && <div className="t-meta">没有负责中的项目。</div>}
            {preview.projects.map((p) => (
              <div key={p.id} className="st-handover">
                <StatusIcon status={p.status} />
                <span className="t-meta">{p.code}</span>
                <span className="ellipsis flex1">{p.name}</span>
                <span className="t-meta">交给</span>
                <Choice
                  value={to[p.id] ?? ''}
                  options={[{ value: '', label: '选择接手人' }, ...candidates.map((m) => ({ value: m.id, label: m.name }))]}
                  onChange={(v) => setTo((x) => ({ ...x, [p.id]: v }))}
                  width={160}
                />
              </div>
            ))}
          </div>
          <div>
            <div className="t-group" style={{ marginBottom: 6 }}>
              进行中的事项
            </div>
            <ul className="st-notes">
              <li>
                {preview.openAnomalies} 条收件箱异常、{preview.openAssignments} 项指派随项目一起转给新负责人
                {preview.planItems ? `；${preview.planItems} 项明日计划留在原日报里` : ''}
              </li>
              <li>历史日报保留在项目动态里，署名不变，只读</li>
              <li>「建议关注」中关于{member.name}的记录和你的备注在交接完成时删除</li>
              <li>{fmtMonthDay(lastDay)} 18:00 起停止提醒并关闭登录</li>
              <li>新负责人会收到通知，并需要确认接手</li>
            </ul>
          </div>
          {error && <div className="t-meta c-red">{error}</div>}
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 导入 CSV
// ---------------------------------------------------------------------------

function ImportModal({ onClose }: { onClose: () => void }) {
  const { importCsv } = useAdminActions();
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<{ created: number; skipped: { line: number; reason: string }[]; unmailed: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      width={560}
      title="导入成员"
      subtitle="每行一人：姓名,邮箱,团队,角色,直属主管"
      onClose={onClose}
      footNote={!result && '导入的人会收到邀请邮件，状态为“待激活”'}
      footer={
        result ? (
          <Button variant="primary" onClick={onClose}>
            完成
          </Button>
        ) : (
          <Button variant="primary" disabled={!csv.trim() || importCsv.isPending} onClick={() => importCsv.mutate(csv, { onSuccess: setResult, onError: (e) => setError(errorText(e)) })}>
            导入
          </Button>
        )
      }
    >
      {result ? (
        <div className="st-form">
          <div className="t-body">
            新增 {result.created} 人{result.skipped.length ? `，跳过 ${result.skipped.length} 行` : ''}。
          </div>
          {result.unmailed.length > 0 && (
            <div className="t-meta">邀请邮件没发出去：{result.unmailed.join('、')}。请把登录地址直接发给他们，他们用自己的邮箱收验证码登录。</div>
          )}
          {result.skipped.length > 0 && (
            <ul className="st-notes">
              {result.skipped.map((s) => (
                <li key={s.line}>
                  第 {s.line} 行：{s.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="st-form">
          <textarea
            className="wd-textarea"
            rows={8}
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={'姓名,邮箱,团队,角色,直属主管\n赵工,zhao@wattsonic.com,售后服务,成员,周总\n钱经理,qian@wattsonic.com,销售部,主管,周总'}
            style={{ fontFamily: 'inherit' }}
          />
          <label className="st-file">
            <Icon name="clip" size={14} />
            从文件读取（.csv）
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void readTextFile(f).then(setCsv);
              }}
            />
          </label>
          <span className="t-meta">团队不存在时会自动新建；角色写“管理员”“主管”或留空（成员）；邮箱已在工作区里的行会跳过。</span>
          {error && <div className="t-meta c-red">{error}</div>}
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 节假日表
// ---------------------------------------------------------------------------

function expand(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to && out.length < 60; d = addDays(d, 1)) out.push(d);
  return out;
}

/** 连续的日期合成一段，方便看 */
function ranges(dates: string[]): [string, string][] {
  const sorted = [...new Set(dates)].sort();
  const out: [string, string][] = [];
  for (const d of sorted) {
    const last = out[out.length - 1];
    if (last && addDays(last[1], 1) === d) last[1] = d;
    else out.push([d, d]);
  }
  return out;
}

export function HolidayModal({ dto, onClose }: { dto: SettingsDTO; onClose: () => void }) {
  const { update } = useSettingsActions();
  const [holidays, setHolidays] = useState(dto.current.holidays);
  const [extra, setExtra] = useState(dto.current.extraWorkdays);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [workday, setWorkday] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [imported, setImported] = useState<string | null>(null);
  const [importing, setImporting] = useState<number | null>(null);
  const year = todayCN().slice(0, 4);
  const ro = !dto.canEdit;
  // 一键导入国务院公布的放假安排：替换这一年已登记的日期，保存后生效
  const importYear = async (y: number) => {
    setImporting(y);
    setError(null);
    try {
      const r = await api<OfficialHolidaysDTO>(`/settings/holidays/official?year=${y}`);
      const other = (d: string) => !d.startsWith(`${y}-`);
      setHolidays((h) => [...h.filter(other), ...r.holidays]);
      setExtra((x) => [...x.filter(other), ...r.extraWorkdays]);
      setImported(`已导入 ${y} 年：${r.names.join('、')}，共放假 ${r.holidays.length} 天、调休上班 ${r.extraWorkdays.length} 天。确认无误后点「保存」。`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setImporting(null);
    }
  };
  const fmt = ([a, b]: [string, string]) => (a === b ? fmtDateWeek(a) : `${fmtMonthDay(a)} – ${fmtMonthDay(b)}（${expand(a, b).length} 天）`);

  return (
    <Modal
      width={520}
      title="节假日表"
      subtitle={`${year} 年 · ${dto.current.followHolidays ? '已启用' : '当前设置为仅周一至周五，节假日表不生效'}`}
      onClose={onClose}
      footNote={ro ? '只有管理员能修改' : '按国务院办公厅每年发布的放假安排填写，修改立即生效'}
      footer={
        !ro && (
          <Button
            variant="primary"
            disabled={update.isPending}
            onClick={() => update.mutate({ holidays: [...new Set(holidays)].sort(), extraWorkdays: [...new Set(extra)].sort() }, { onSuccess: onClose, onError: (e) => setError(errorText(e)) })}
          >
            保存
          </Button>
        )
      }
    >
      <div className="st-form">
        {!ro && (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {[Number(year), Number(year) + 1].map((y) => (
              <Button key={y} icon="cal" disabled={importing !== null} onClick={() => void importYear(y)}>
                {importing === y ? '正在导入…' : `导入 ${y} 年法定节假日`}
              </Button>
            ))}
          </div>
        )}
        {imported && <div className="t-meta">{imported}</div>}
        <div>
          <div className="t-group" style={{ marginBottom: 6 }}>
            放假
          </div>
          {ranges(holidays).length === 0 && <div className="t-meta">还没有登记。没有登记时，只有周六、周日不算工作日。</div>}
          <div className="st-chips">
            {ranges(holidays).map((r) => (
              <span key={r[0]} className="st-chip">
                {fmt(r)}
                {!ro && <IconButton name="close" label="删除" onClick={() => setHolidays((h) => h.filter((d) => d < r[0] || d > r[1]))} />}
              </span>
            ))}
          </div>
          {!ro && (
            <div className="row" style={{ gap: 8, marginTop: 8 }}>
              <input className="wd-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="开始" />
              <span className="t-meta">至</span>
              <input className="wd-input" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} aria-label="结束" />
              <Button
                disabled={!from}
                onClick={() => {
                  const days = expand(from, to && to >= from ? to : from);
                  setHolidays((h) => [...h, ...days]);
                  setExtra((x) => x.filter((d) => !days.includes(d)));
                  setFrom('');
                  setTo('');
                }}
              >
                添加
              </Button>
            </div>
          )}
        </div>
        <div>
          <div className="t-group" style={{ marginBottom: 6 }}>
            调休上班（周末）
          </div>
          <div className="st-chips">
            {[...new Set(extra)].sort().map((d) => (
              <span key={d} className="st-chip">
                {fmtDateWeek(d)}
                {!ro && <IconButton name="close" label="删除" onClick={() => setExtra((x) => x.filter((y) => y !== d))} />}
              </span>
            ))}
            {extra.length === 0 && <span className="t-meta">没有</span>}
          </div>
          {!ro && (
            <div className="row" style={{ gap: 8, marginTop: 8 }}>
              <input className="wd-input" type="date" value={workday} onChange={(e) => setWorkday(e.target.value)} aria-label="调休上班日" />
              <Button
                disabled={!workday}
                onClick={() => {
                  setExtra((x) => [...x, workday]);
                  setHolidays((h) => h.filter((d) => d !== workday));
                  setWorkday('');
                }}
              >
                添加
              </Button>
            </div>
          )}
        </div>
        {error && <div className="t-meta c-red">{error}</div>}
      </div>
    </Modal>
  );
}
